/**
 * SkipLinks Component - Accessibility skip navigation links
 * 
 * Provides keyboard users with quick navigation to main content areas,
 * bypassing repetitive navigation elements.
 * 
 * Features:
 * - Skip to main content
 * - Skip to navigation
 * - Skip to search
 * - Visible on focus only
 * - WCAG AA compliant
 */

import React from 'react';
import { clsx } from 'clsx';

export interface SkipLinksProps {
  /**
   * Additional skip link targets
   */
  additionalLinks?: Array<{
    href: string;
    label: string;
  }>;
}

export const SkipLinks: React.FC<SkipLinksProps> = ({ additionalLinks = [] }) => {
  const defaultLinks = [
    { href: '#main-content', label: 'Skip to main content' },
    { href: '#main-navigation', label: 'Skip to navigation' },
    { href: '#search', label: 'Skip to search' }
  ];
  
  const links = [...defaultLinks, ...additionalLinks];
  
  return (
    <div className="sr-only focus-within:not-sr-only">
      <div className="absolute top-0 left-0 right-0 z-[100] bg-surface-primary p-sp-2">
        <nav aria-label="Skip links">
          <ul className="flex flex-wrap gap-sp-2">
            {links.map((link) => (
              <li key={link.href}>
                <a
                  href={link.href}
                  className={clsx(
                    'inline-block px-sp-4 py-sp-2',
                    'bg-brand-primary text-white',
                    'rounded-md',
                    'font-medium',
                    'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2',
                    'hover:bg-brand-primary-dark',
                    'transition-colors'
                  )}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
};

SkipLinks.displayName = 'SkipLinks';